import React, { useCallback } from 'react'
import styles from '../index.module.scss'
import { Spinner } from '../../../pages/shared/Spinner'
import QUERY_KEYS from '../../../graphql/queryKeys'
import { ArrayParam, BooleanParam, StringParam, useQueryParams } from 'use-query-params'
import useQuery from '../../../hooks/useQuery'
import { captureErrorAndNotify } from '../../../utilities/errorHandlers'
import reportsTicketDetailsQuery from '../../../graphql/queries/reportsTicketDetails'
import reportsFiltersQuery from '../../../graphql/queries/reportsFilters'
import ReportsFilterForm from '../reports-filter-form'
import TicketDetailsTable from './TicketDetailsTables'

function TicketDetails () {
  const [queryParams, setQueryParams] = useQueryParams({
    startDate: StringParam,
    endDate: StringParam,
    resourceTypes: ArrayParam,
    drivers: ArrayParam,
    status: ArrayParam,
    yearToDate: BooleanParam
  })
  // Curried function to make sure we are always using 'replace' update.
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { data: reportData, isFetching } = useQuery([
    QUERY_KEYS.reportsTicketDetails,
    queryParams.startDate || null,
    queryParams.endDate || null,
    queryParams.resourceTypes?.length > 0 ? queryParams.resourceTypes : null,
    queryParams.drivers?.length > 0 ? queryParams.drivers : null,
    queryParams.status?.length > 0 ? queryParams.status : null,
    queryParams.yearToDate
  ],
  reportsTicketDetailsQuery,
  {
    initialData: { reportsTicketDetails: [] },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch reports data')
    }
  })

  const { data: { reportsFilters = [] } = {}, isFetching: isFetchingReportsFilters } = useQuery(
    [QUERY_KEYS.reportsFilters],
    reportsFiltersQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      },
      placplaceholderData: {
        reportFilters: {
          resources: [], ticketTypes: [], drivers: []
        }
      }
    }
  )

  const getHeaders = () => {
    const tempHeaders = reportData?.reportsTicketDetails?.map((item) => item?.ticketType)
    return Array.from(new Set(tempHeaders))
  }

  return (
    <div className={styles.reportsDiv}>
      <div>
        <ReportsFilterForm
          handleInputChange={setQueryParamsReplace}
          showOptions={['dateRange', 'drivers', 'yearToDate', 'status', 'resourceTypes']}
          options={{
            resourceTypes: reportsFilters?.resources?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.name
            })) || [],
            drivers: reportsFilters?.drivers?.map(opt => ({
              label: `${opt.name}`,
              value: opt.name
            })) || []
          }}
          initialValues={queryParams}
          clearSearch={() => setQueryParamsReplace({
            startDate: undefined,
            endDate: undefined,
            resourceTypes: undefined,
            drivers: undefined,
            status: undefined,
            yearToDate: undefined
          })}
        />
      </div>
      <div className={styles.reportCharts}>
        <Spinner isFetching={reportData === undefined || isFetching || isFetchingReportsFilters}>
          <TicketDetailsTable
            headers={getHeaders()}
            data={reportData?.reportsTicketDetails || []}
          />
        </Spinner>
      </div>
    </div>
  )
}

export default TicketDetails
